import { z } from 'zod';
import { PlayerIdSchema } from '../ids';
import { CompanyRankSchema } from './company';

/**
 * The members' access (CORE-PERMISSION, ADR 0085): what someone besides an owner may do with a good
 * it shares. Each kind of good declares its uses; a grant adds uses, never takes any away.
 */

/** A lot's uses that may be granted (GA-10): its other rights stay its owner's and its ranks'. */
export const LOT_GRANTABLE_USES = ['enter', 'store', 'build'] as const;
/** A ship's uses (GA-3): its helm, putting into its hold and taking out of it. */
export const SHIP_USES = ['steer', 'hold_put', 'hold_take'] as const;

export const AccessUseSchema = z.enum([...LOT_GRANTABLE_USES, ...SHIP_USES]);
export type AccessUse = z.infer<typeof AccessUseSchema>;

/** The kinds of good that take grants, and the uses each may grant. */
export const ACCESS_USES = {
  lot: LOT_GRANTABLE_USES,
  ship: SHIP_USES,
} as const satisfies Record<string, readonly AccessUse[]>;
export type AccessKind = keyof typeof ACCESS_USES;

/**
 * To whom a grant is made (GA-1): a character by name, its owner's party while its owner leads it,
 * or its owner's company's members of a rank or above.
 */
export const GranteeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('player'), playerId: PlayerIdSchema }),
  z.object({ kind: z.literal('party') }),
  z.object({ kind: z.literal('rank'), rank: CompanyRankSchema }),
]);
export type Grantee = z.infer<typeof GranteeSchema>;

/** At most this many grants a good holds (bounded payloads, CORE-PERMISSION T2). */
export const MAX_GRANTS = 20;

export const AccessGrantSchema = z.object({
  grantee: GranteeSchema,
  uses: z
    .array(AccessUseSchema)
    .min(1)
    .max(SHIP_USES.length + LOT_GRANTABLE_USES.length),
});
export type AccessGrant = z.infer<typeof AccessGrantSchema>;

/** A grant as its good's managers see it: its grantee's name too, when a character. */
export const AccessGrantViewSchema = AccessGrantSchema.extend({
  name: z.string().nullable(),
});
export type AccessGrantView = z.infer<typeof AccessGrantViewSchema>;
